// List of cardio sessions that have a parseable media file. Clicking
// a row expands it to show the route on a map below.

import { useEffect, useState } from 'react';
import { RouteMap } from './RouteMap';
import css from './CardioRoutes.module.css';

interface CardioRow {
  id: number;
  date: string;
  source: string;
  notes: string | null;
  active_exercise: string | null;
  started_at: number;
  ended_at: number | null;
  media_path: string;
  duration_sec: number | null;
  distance_m: number | null;
  speed_kmh: number | null;
  avg_hr_bpm: number | null;
  peak_hr_bpm: number | null;
  elevation_gain_m: number | null;
}

interface RouteData {
  session: {
    id: number;
    date: string;
    notes: string | null;
    active_exercise: string | null;
  };
  set: {
    duration_sec: number | null;
    distance_m: number | null;
    speed_kmh: number | null;
    avg_hr_bpm: number | null;
    peak_hr_bpm: number | null;
    elevation_gain_m: number | null;
  };
  format: 'gpx' | 'fit' | 'fit.gz';
  point_count: number;
  bounds: { minLat: number; maxLat: number; minLon: number; maxLon: number } | null;
  points: { lat: number; lon: number; elevation?: number; time?: number; heart_rate?: number }[];
}

function fmtDuration(sec: number | null): string {
  if (sec == null) return '—';
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem === 0 ? `${h}h` : `${h}h ${rem}m`;
}

export function CardioRoutes({ data }: { data: CardioRow[] }) {
  const [openId, setOpenId] = useState<number | null>(null);
  const [route, setRoute] = useState<RouteData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (openId == null) {
      setRoute(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setRoute(null);
    fetch(`/api/stats/route/${openId}`)
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((d) => {
        if (!cancelled) setRoute(d);
      })
      .catch(() => {
        if (!cancelled) setRoute(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [openId]);

  if (data.length === 0) {
    return <div className={css.empty}>no cardio sessions with route data.</div>;
  }

  return (
    <ul className={css.list}>
      {data.map((s) => {
        const isOpen = openId === s.id;
        return (
          <li
            key={s.id}
            className={`${css.item}${isOpen ? ` ${css.itemOpen}` : ''}`}
          >
            <button
              type="button"
              className={css.header}
              onClick={() => setOpenId(isOpen ? null : s.id)}
            >
              <div className={css.headerLeft}>
                <div className={css.date}>{s.date}</div>
                <div className={css.title}>{s.notes || s.active_exercise || '(unnamed)'}</div>
              </div>
              <div className={css.headerRight}>
                {s.distance_m != null && (
                  <span className={css.stat}>{(s.distance_m / 1000).toFixed(2)} km</span>
                )}
                <span className={css.stat}>{fmtDuration(s.duration_sec)}</span>
                {s.peak_hr_bpm != null && (
                  <span className={css.statDim}>peak HR {s.peak_hr_bpm}</span>
                )}
                <span className={css.caret}>{isOpen ? '▴' : '▾'}</span>
              </div>
            </button>
            {isOpen && (
              <div className={css.body}>
                {loading && <div className={css.loading}>loading route…</div>}
                {route && (
                  <>
                    <div className={css.summary}>
                      <span>{route.point_count.toLocaleString()} GPS points</span>
                      <span className={css.dim}>format: {route.format}</span>
                      {route.set.avg_hr_bpm && (
                        <span className={css.dim}>
                          avg HR {route.set.avg_hr_bpm} · peak {route.set.peak_hr_bpm ?? '—'}
                        </span>
                      )}
                      {route.set.elevation_gain_m != null && (
                        <span className={css.dim}>+{route.set.elevation_gain_m}m elevation</span>
                      )}
                    </div>
                    <RouteMap points={route.points} bounds={route.bounds} />
                  </>
                )}
                {!loading && !route && (
                  <div className={css.errorMsg}>couldn't load route data for this session.</div>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
